import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DashboardPeriod,
  EmailMetricsTrendResponse,
  useEmailRegionalDashboard,
} from '@/hooks/use-email-dashboard';
import { formatDate, formatNumber } from '@/lib/utils';

const PERIOD_OPTIONS: DashboardPeriod[] = ['24h', '7d', '30d'];

function statusVariant(slo: number): 'default' | 'secondary' | 'destructive' {
  if (slo >= 99.5) {
    return 'default';
  }
  if (slo >= 99.3) {
    return 'secondary';
  }
  return 'destructive';
}

function statusLabel(slo: number): string {
  if (slo >= 99.5) {
    return 'HEALTHY';
  }
  if (slo >= 99.3) {
    return 'MONITOR';
  }
  return 'DEGRADED';
}

function latestTrendPoint(trend: EmailMetricsTrendResponse | undefined) {
  if (!trend || trend.points.length === 0) {
    return null;
  }
  return trend.points[trend.points.length - 1];
}

export function EmailRegionalBreakdownPanel() {
  const [period, setPeriod] = useState<DashboardPeriod>('7d');
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const { data, isLoading, error } = useEmailRegionalDashboard(period);

  const metrics = data?.metrics ?? [];
  const trends = data?.trends ?? [];

  const trendByRegion = useMemo(() => {
    const map = new Map<string, EmailMetricsTrendResponse>();
    for (const trend of trends) {
      map.set(trend.region, trend);
    }
    return map;
  }, [trends]);

  const availableRegions = useMemo(() => {
    return Array.from(new Set(metrics.map((metric) => metric.region))).sort();
  }, [metrics]);

  const chartSeries = useMemo(() => {
    if (selectedRegion !== 'all') {
      const trend = trendByRegion.get(selectedRegion);
      return trend?.points.map((point) => ({
        label: formatDate(point.timestamp),
        slo: point.slo_percent,
        emails: point.emails_sent,
      })) ?? [];
    }

    const merged = new Map<string, { label: string; totalSlo: number; count: number; emails: number }>();
    for (const trend of trends) {
      for (const point of trend.points) {
        const key = point.timestamp;
        const current = merged.get(key) ?? {
          label: formatDate(point.timestamp),
          totalSlo: 0,
          count: 0,
          emails: 0,
        };
        current.totalSlo += point.slo_percent;
        current.count += 1;
        current.emails += point.emails_sent;
        merged.set(key, current);
      }
    }

    return Array.from(merged.values())
      .map((value) => ({
        label: value.label,
        slo: value.count > 0 ? Number((value.totalSlo / value.count).toFixed(2)) : 0,
        emails: value.emails,
      }));
  }, [selectedRegion, trendByRegion, trends]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <CardTitle>Email Regional Breakdown</CardTitle>
          <div className="flex gap-2">
            <Select value={period} onValueChange={(value: DashboardPeriod) => setPeriod(value)}>
              <SelectTrigger className="w-[110px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                {availableRegions.map((region) => (
                  <SelectItem key={region} value={region}>
                    {region.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {isLoading ? (
          <>
            <Skeleton className="h-56 w-full rounded-lg" />
            <Skeleton className="h-36 w-full rounded-lg" />
          </>
        ) : error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            Failed to load email regional metrics.
          </div>
        ) : (
          <>
            <div className="h-56 w-full">
              {chartSeries.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  No trend data available for selected filters.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis domain={[98.5, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="slo" stroke="#2563eb" strokeWidth={2} dot={false} name="SLO %" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px]">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 font-medium">Region</th>
                    <th className="py-2 text-right font-medium">SLO %</th>
                    <th className="py-2 text-right font-medium">Emails</th>
                    <th className="py-2 text-right font-medium">Bounce %</th>
                    <th className="py-2 text-right font-medium">Complaint %</th>
                    <th className="py-2 text-right font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics
                    .filter((metric) => selectedRegion === 'all' || metric.region === selectedRegion)
                    .map((metric) => {
                      const trend = trendByRegion.get(metric.region);
                      const latest = latestTrendPoint(trend);
                      const slo = latest?.slo_percent ?? metric.slo_percent;

                      return (
                        <tr key={metric.region} className="border-b last:border-0">
                          <td className="py-3 text-sm font-medium">{metric.region.toUpperCase()}</td>
                          <td className="py-3 text-right text-sm">{slo.toFixed(2)}</td>
                          <td className="py-3 text-right text-sm">{formatNumber(metric.emails_sent)}</td>
                          <td className="py-3 text-right text-sm">{metric.bounce_rate.toFixed(2)}</td>
                          <td className="py-3 text-right text-sm">{metric.complaint_rate.toFixed(2)}</td>
                          <td className="py-3 text-right">
                            <Badge variant={statusVariant(slo)}>{statusLabel(slo)}</Badge>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
